export const UTC_INSTANT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;

export type MigrationContext = {
  migratedAt: string;
};

export type MigrationIssue = {
  path: string;
  code: string;
};

export type SnapshotCounts = {
  words: number;
  meanings: number;
  relations: number;
  exerciseSets: number;
  practiceStates: number;
};

export type StorageFileKind = "snapshot" | "backup";

export type MigrationReport = {
  sourceVersion: number;
  targetVersion: number;
  appliedStepIds: string[];
  stepCount: number;
  sourceByteSize: number;
  targetByteSize: number;
  migratedAt: string;
  noop: boolean;
  fileKind: StorageFileKind;
  backupExportedAt?: string;
  counts: SnapshotCounts;
};

export type InspectSuccess = {
  status: "ok";
  fileKind: StorageFileKind;
  sourceVersion: number;
  targetVersion: number;
  availableStepIds: string[];
  backupExportedAt?: string;
};

export type FileDecodeFailure =
  | { status: "malformed_json"; message: string }
  | { status: "invalid_version"; message: string; issues?: MigrationIssue[] }
  | { status: "invalid_wrapper"; message: string; issues?: MigrationIssue[] }
  | {
      status: "invalid_source";
      message: string;
      sourceVersion?: number;
      issues?: MigrationIssue[];
    }
  | { status: "unsupported_future"; message: string; version: number }
  | { status: "unsupported_old"; message: string; version: number };

export type InspectResult = InspectSuccess | FileDecodeFailure;

export type MigrateSuccess = {
  status: "ok";
  snapshot: unknown;
  serializedSnapshot: string;
  report: MigrationReport;
};

export type MigrateFailure =
  | FileDecodeFailure
  | {
      status: "conversion_failed";
      message: string;
      stepId?: string;
      sourceVersion: number;
      issues?: MigrationIssue[];
    }
  | {
      status: "invalid_intermediate";
      message: string;
      stepId: string;
      issues?: MigrationIssue[];
    }
  | { status: "invalid_target"; message: string; issues?: MigrationIssue[] }
  | { status: "invalid_context"; message: string };

export type MigrateResult = MigrateSuccess | MigrateFailure;

export function isUtcInstant(value: string): boolean {
  if (!UTC_INSTANT_PATTERN.test(value)) {
    return false;
  }
  const milliseconds = Date.parse(value);
  return !Number.isNaN(milliseconds);
}

export function parseMigrationContext(migratedAt: string): MigrationContext | { error: string } {
  if (!isUtcInstant(migratedAt)) {
    return { error: "migratedAt must be a UTC instant such as 2026-01-01T00:00:00.000Z." };
  }
  return { migratedAt };
}
